import React, { FC, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
  styled,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Cookies from 'js-cookie'
import config from '../../config'
import {
  CreateCustomerSupportRequestMutationVariables,
  useCreateCustomerSupportRequestMutation,
} from '../../graphql'
import { CRYPTO_PRODUCT_STATUS } from '../../constants'

const StyledFooterBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  padding: theme.spacing(3),
  [theme.breakpoints.down('xs')]: {
    flexDirection: 'column-reverse',
  },
  '& .MuiButton-root': {
    minWidth: '50px',
    '&:last-child': {
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1.5),
      },
    },
    '&:first-child': {
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(1),
      },
    },
  },
}))

type ModalConfirmProps = {
  handleClose: (requestSent: boolean) => void
  isOpen: boolean
  applicationId: string | number
}

export const EnableCryptoProductInfoModal: FC<ModalConfirmProps> = ({
  handleClose,
  isOpen,
  applicationId,
}) => {
  const { t } = useTranslation()

  const [requestSent, setRequestSent] = useState(false)

  useEffect(() => {
    const valueFromCookies = Cookies.get(`${CRYPTO_PRODUCT_STATUS}-${applicationId}`) === 'sent'
    setRequestSent(valueFromCookies)
  }, [])

  const [
    createCustomerSupportRequestMutation,
    { loading },
  ] = useCreateCustomerSupportRequestMutation()

  const requestCryptoProduct = useCallback(async () => {
    try {
      const mutationParams: CreateCustomerSupportRequestMutationVariables = {
        title: 'cryptoAccountRequest',
        contractId: applicationId,
        details: '',
        topic: 'Stablecoin-Fiat Exchange Product Request',
      }
      await createCustomerSupportRequestMutation({
        variables: mutationParams,
      })
      setRequestSent(true)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }, [applicationId])

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={(theme) => ({
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[5],
          [theme.breakpoints.up('sm')]: {
            maxWidth: 590,
          },
          [theme.breakpoints.down('xs')]: {
            maxWidth: 'calc(100% - 32px)',
          },
          maxHeight: '90vh',
          overflowY: 'auto',
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            padding: theme.spacing(3, 3, 2),
            alignItems: 'center',
            '& > svg': {
              marginRight: 15,
            },
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant={'h4'}>
              {requestSent
                ? t('stablecoinFiatExchange', 'Stablecoin-Fiat Exchange')
                : t('enableCrypto', 'Enable Stablecoin-Fiat Exchange')}
            </Typography>
          </Box>
          <Button
            sx={{
              minWidth: 'auto',
              minHeight: 'auto',
            }}
            onClick={() => handleClose(requestSent)}
          >
            <CloseIcon fontSize={'small'} />
          </Button>
        </Box>

        {requestSent && (
          <Box
            sx={(theme) => ({
              padding: theme.spacing(0, 3, 3, 3),
            })}
          >
            <Typography>
              {t(
                'cryptoRequestReceived',
                'Your request to enable Stablecoin-Fiat Exchange has been received. Our team will review your request and get back to you as soon as possible.',
              )}
            </Typography>
          </Box>
        )}

        {!requestSent && (
          <>
            <Box
              sx={(theme) => ({
                padding: theme.spacing(0, 3),
              })}
            >
              <Typography component="p">
                {t(
                  'exchangeDirectly',
                  'Exchange USDT, USDC & ETH directly between your ERC20 wallet and {{val1}} EUR or USD Accounts.',
                  { val1: config.name },
                )}
              </Typography>
              <Typography>{t('keyBenefits', 'Key benefits include:')}</Typography>
              <List disablePadding>
                {[
                  t(
                    'fasterProcessing',
                    'Faster Processing - No delays in settlements from Stablecoin Exchanges.',
                  ),
                  t(
                    'simplerProcess',
                    'Simpler Process - No multiple accounts, KYC, or extra hurdles.',
                  ),
                  t('lowerCosts', 'Lower Costs - No transaction charges, just one conversion fee.'),
                ].map((item, i) => (
                  <ListItem sx={{ padding: 0 }} key={i}>
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <ListItemIcon sx={{ minWidth: '20px', color: 'black' }}>{'✔️'}</ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              <Card
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 2.5,
                  marginBottom: 2.5,
                  maxWidth: '450px',
                  height: '250px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                <CardMedia
                  component="iframe"
                  src={config.crypto.learnVideoLink}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Card>
              <Typography>
                {
                  <Trans
                    i18nKey="clickEnableProduct"
                    t={t}
                    values={{ val1: config.name }}
                    components={{ bold: <strong /> }}
                  />
                }
              </Typography>
            </Box>

            <StyledFooterBox>
              <Button
                variant="contained"
                color="primary"
                onClick={requestCryptoProduct}
                disableElevation
                disabled={loading}
              >
                {t('enableProduct', 'Enable Product')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                sx={{ color: '#000' }}
                onClick={() => handleClose(requestSent)}
                disableElevation
              >
                {t('cancel', 'Cancel')}
              </Button>
            </StyledFooterBox>
          </>
        )}
      </Box>
    </Modal>
  )
}
